import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { createEditTask, getTasks } from "../api/apiTaskManagerHistory";
import toast from "react-hot-toast";
import "./History.scss";
// Material UI
import { Box, Tabs, Tab, Divider, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
// Components
import ServicesGrid from "../components/home/serviceslist/ServicesGrid";
import TaskManager from "../components/home/taskmanager/TaskManager";
import TaskInput from "../components/home/taskinput/TaskInput";
import TasksListAuthorize from "../components/home/taskslist/TasksListAuthorize";
import TasksListMain from "../components/home/taskslist/TasksListMain";
import TaskManager_AdminMenu from "../components/home/taskmanager/TaskManager_AdminMenu";
import HistoryDatePicker from "../components/history/HistoryDatePicker";
import GridLayout from "../ui/GridLayout";
// Import Supabase client
import supabase from "../api/supabase";
// bottom slider
import { useSettings } from "../hook/useSettings"; // Import the custom hook

function History() {
  // TODO Material UI
  const [activeTab, setActiveTab] = useState(0); // New state to track active tab

  // Set the active tab
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // TODO State variables
  const [taskRecord, setTaskRecord] = useState([]); // State for task records
  const [activeCell, setActiveCell] = useState(null); // Active cell state
  const [taskInput, setTaskInput] = useState(null); // State for task input
  const [activeName, setActiveName] = useState(null); // Active name state

  // Fetch the current user's roles using the hook

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm")); // Detect small screens
  // Use custom hook to fetch settings
  const {
    settingsData,
    loading: loadingSettings,
    errorSettings,
  } = useSettings();

  // Handle grid layout based on themeGrid value
  const themeGrid = settingsData?.themeGrid;

  // Manage Date
  const [date, setDate] = useState(() => {
    const today = new Date();
    return today; // Keep the Date object for DatePicker
  });

  // Format the date to YYYY-MM-DD for query and display purposes
  const formattedDate = date.toLocaleDateString("en-CA"); // 'en-CA' gives YYYY-MM-DD format in local time
  console.log("Formatted Date:", formattedDate);

  // Setup react-query's queryClient for cache management
  const queryClient = useQueryClient();

  // Fetch taskManager_history from the database based on the selected date
  const {
    isLoading: isLoadingTasks,
    data: tasksData,
    error: errorTasks,
  } = useQuery({
    queryKey: ["taskManager_history", formattedDate], // Use formatted date in query key
    queryFn: () => getTasks(formattedDate), // Pass the formatted date to getTasks
    enabled: !!formattedDate, // Only fetch when the date is set
    placeholderData: keepPreviousData, // Keep UI mounted while switching dates
  });

  // Initialize taskRecord with fetched tasksData
  useEffect(() => {
    if (tasksData) {
      setTaskRecord(tasksData);
    }
  }, [tasksData]);

  // Add Supabase real-time subscription for taskManager_history table
  useEffect(() => {
    const channel = supabase
      .channel("taskManager_history:realtime")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "taskManager_history" },
        () => {
          queryClient.invalidateQueries({
            queryKey: ["taskManager_history", formattedDate],
          });
          queryClient.invalidateQueries({
            queryKey: ["taskManager_history_dates"],
          });
        }
      )
      .subscribe();

    // Cleanup the subscription on component unmount
    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient, formattedDate]);

  // Mutation to update or create a task in the taskManager_history database
  const updateTaskPromise = (finalRecord) => {
    return new Promise((resolve, reject) => {
      updateTask(finalRecord, {
        onSuccess: () => resolve(),
        onError: (error) => reject(error),
      });
    });
  };

  const { mutate: updateTask } = useMutation({
    mutationFn: createEditTask,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["taskManager_history", formattedDate],
      });
      queryClient.invalidateQueries({
        queryKey: ["taskManager_history_dates"],
      });
    },
    onError: (error) => {
      console.error("Error creating or updating task:", error);
    },
  });

  // Only block the page on the first load — date changes must not unmount the picker
  if ((isLoadingTasks && !tasksData) || loadingSettings) {
    return <div>Loading...</div>;
  }

  if (errorTasks || errorSettings) {
    toast.error(errorTasks?.message || errorSettings?.message || "Error");
    return <div>Error loading tasks</div>;
  }

  // Handle cell click events
  const handleCellClick = (recordIndex, taskKey, selectedTask) => {
    setActiveCell({ recordIndex, taskKey });
    setActiveName(taskRecord[recordIndex].name);
    setTaskInput({
      ...selectedTask,
      avatar: taskRecord[recordIndex].avatar,
      name: taskRecord[recordIndex].name,
      column: taskKey,
    });
  };

  // Update service click with toast.promise
  const handleServiceClick = (selectedService) => {
    if (activeCell) {
      const updatedRecords = taskRecord.map((record, index) => {
        if (index === activeCell.recordIndex) {
          const updatedTask = {
            ...record[activeCell?.taskKey],
            ...selectedService,
          };

          const updatedRecord = {
            ...record,
            [activeCell.taskKey]: updatedTask,
          };

          // Calculate new totals for credits, income, and supplies
          const newCredits = [...Array(20).keys()].reduce((total, i) => {
            return total + (updatedRecord[`t${i + 1}`].serCredit || 0);
          }, 0);

          const newIncome = [...Array(20).keys()].reduce((total, i) => {
            return total + (updatedRecord[`t${i + 1}`].serAmount || 0);
          }, 0);

          const newSupplies = [...Array(20).keys()].reduce((total, i) => {
            return total + (updatedRecord[`t${i + 1}`]?.serSupply || 0);
          }, 0);

          const finalRecord = {
            ...updatedRecord,
            credits: newCredits,
            income: newIncome,
            supplies: newSupplies,
            id: record.id, // Ensure 'id' is included
          };

          // Use toast.promise to handle success and error notifications
          toast.promise(updateTaskPromise(finalRecord), {
            loading: "Saving...",
            success: "Task saved!",
            error: "Could not save.",
          });

          return finalRecord;
        }
        return record;
      });

      setTaskRecord(updatedRecords);
      setTaskInput({
        ...selectedService,
        avatar: taskRecord[activeCell.recordIndex].avatar,
        name: taskRecord[activeCell.recordIndex].name,
        column: activeCell.taskKey,
      });
    }
  };

  // Update save with toast.promise
  const handleSave = (updatedTask) => {
    if (activeCell) {
      const updatedRecords = taskRecord.map((record, index) => {
        if (index === activeCell.recordIndex) {
          const updatedRecord = {
            ...record,
            [activeCell.taskKey]: {
              ...record[activeCell.taskKey],
              ...updatedTask,
            },
          };

          // Recalculate the total income and supplies for the row
          const newIncome = [...Array(20).keys()].reduce((total, i) => {
            return total + (updatedRecord[`t${i + 1}`]?.serAmount || 0);
          }, 0);

          const newSupplies = [...Array(20).keys()].reduce((total, i) => {
            return total + (updatedRecord[`t${i + 1}`]?.serSupply || 0);
          }, 0);

          const finalRecord = {
            ...updatedRecord,
            income: newIncome,
            supplies: newSupplies,
          };

          // Use toast.promise for save operation
          toast.promise(updateTaskPromise(finalRecord), {
            loading: "Saving...",
            success: "Task saved!",
            error: "Could not save.",
          });

          return finalRecord;
        }
        return record;
      });

      setTaskRecord(updatedRecords);
    }
  };

  // Handle name click events
  const handleNameClick = (name) => {
    setActiveName(name);
    const index = taskRecord.findIndex((record) => record.name === name);
    setActiveCell({ recordIndex: index });
  };

  // Handle date change from DatePicker
  const handleDateChange = (date) => {
    // Use the original Date object for DatePicker
    setDate(date);
  };

  const Grid1Content = (
    <div className="dashboardContainer">
      <TaskManager
        taskRecord={taskRecord}
        onCellClick={handleCellClick}
        activeCell={activeCell}
        activeName={activeName}
        headerEnd={
          <HistoryDatePicker selected={date} onChange={handleDateChange} />
        }
      />
      {isSmallScreen && !themeGrid && (
        <TaskManager_AdminMenu
          tab1={
            <TasksListMain
              taskRecord={taskRecord}
              activeName={activeName}
              activeCell={activeCell} // Pass active cell state for Employee Status
              setTaskRecord={setTaskRecord} // Pass state update function for Employee Status
              onNameClick={handleNameClick}
            />
          }
          tab2={<ServicesGrid onServiceClick={handleServiceClick} />}
          tab3={<TaskInput taskInput={taskInput} onSave={handleSave} />}
          tab4={
            <TasksListAuthorize
              taskRecord={taskRecord}
              activeName={activeName}
              onNameClick={handleNameClick}
            />
          }
        />
      )}
    </div>
  );

  const Grid2Content = (
    <>
      {/* Use Material-UI Tabs */}
      <Box sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="basic tabs example"
          sx={{
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: "bold",
              color: "gray",
              borderBottom: "1px solid lightgray",
              minWidth: 100,
              "&.Mui-selected": {
                color: "#ff5722",
                borderTopRightRadius: 5,
                borderTopLeftRadius: 5,
                border: "1px solid lightgray",
              },
              "&:hover": {
                color: "black",
              },
            },
            "& .MuiTabs-indicator": {
              backgroundColor: "#f3f3f3",
            },
          }}
        >
          <Tab label="MANAGER" />
          <Tab label="ALL" />
        </Tabs>
      </Box>

      {/* Material-UI TabPanels */}
      {activeTab === 0 && (
        <Box >
          <TasksListAuthorize
            taskRecord={taskRecord}
            activeName={activeName}
            onNameClick={handleNameClick}
          />
          <Divider sx={{ marginTop: 2, marginBottom: 2 }}>MANAGER</Divider>
          <TaskInput taskInput={taskInput} onSave={handleSave} />
        </Box>
      )}
      {activeTab === 1 && (
        <Box sx={{ padding: 2 }}>
          <TasksListMain
            taskRecord={taskRecord}
            activeName={activeName}
            onNameClick={handleNameClick}
          />
        </Box>
      )}

      <Divider sx={{ marginBottom: 2 }}>SERVICE LIST</Divider>
      <ServicesGrid onServiceClick={handleServiceClick} />
    </>
  );

  return (
    <GridLayout Grid1={Grid1Content} Grid2={Grid2Content} HideGrid2={true} />
  );
}

export default History;
